import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { rand, monthLabel } from '@/components/stokvel/format';

const KEYS = [
  ['contribution', 'Deduction'], ['company', 'Company'], ['interest', 'Interest'],
  ['withdrawal', 'Emergency withdrawal'], ['penalty', 'Penalty'], ['refund', 'Exit refund'],
];

const exportXlsx = (data, from, to) => {
  const head = ['Name', 'Employee Code', 'Monthly Deduction', 'Monthly Company', 'First Payroll Month', 'Status'];
  data.months.forEach(m => KEYS.forEach(([, l]) => head.push(`${monthLabel(m)} ${l}`)));
  KEYS.forEach(([, l]) => head.push(`Total ${l}`));
  const rows = data.rows.map(r => {
    const row = [r.name, r.employee_code, r.monthly_amount, r.company_amount, r.first_month, r.status];
    data.months.forEach(m => KEYS.forEach(([k]) => row.push(r.months[m][k])));
    KEYS.forEach(([k]) => row.push(r.totals[k]));
    return row;
  });
  const tot = ['TOTALS', '', '', '', '', ''];
  data.months.forEach(m => KEYS.forEach(([k]) => tot.push(data.rows.reduce((s, r) => s + r.months[m][k], 0))));
  KEYS.forEach(([k]) => tot.push(data.rows.reduce((s, r) => s + r.totals[k], 0)));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...rows, tot]), 'Stokvel');
  XLSX.writeFile(wb, `TMC_Stokvel_Report_${from}_to_${to}.xlsx`);
};

const PREFIX = { company: 'C', interest: 'I', withdrawal: 'E', penalty: 'P', refund: 'R' };

const Cell = ({ v }) => {
  const parts = KEYS.filter(([k]) => v[k]).map(([k]) => (
    <div key={k} className={k === 'contribution' ? '' : k === 'withdrawal' || k === 'penalty' || k === 'refund' ? 'text-rose-700' : 'text-emerald-700'}>
      {k === 'contribution' ? '' : `${PREFIX[k]} `}{rand(v[k])}
    </div>
  ));
  return parts.length ? parts : <span className="text-stone-300">—</span>;
};

export const StokvelReportPanel = () => {
  const y = new Date().getFullYear();
  const [range, setRange] = useState({ from: `${y}-01`, to: `${y}-12` });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setData((await axios.get(`${API}/stokvel/admin/report`, { params: { from_month: range.from, to_month: range.to } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From month</Label><Input type="month" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} data-testid="stokvel-report-from" /></div>
        <div><Label className="text-xs">To month</Label><Input type="month" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} data-testid="stokvel-report-to" /></div>
        <Button onClick={load} disabled={busy} className="bg-stone-800 text-white" data-testid="stokvel-report-run">{busy ? 'Loading…' : 'Run report'}</Button>
        {data && <Button variant="outline" onClick={() => exportXlsx(data, range.from, range.to)} data-testid="stokvel-report-export"><Download className="w-4 h-4 mr-1" />Export Excel</Button>}
      </div>
      <p className="text-xs text-stone-500">Months are payroll months (26th–25th, paid on the 25th). Black = deduction, C = company, I = interest, E = emergency withdrawal (payout month), P = penalty, R = exit refund.</p>
      {data && (
        <div className="overflow-x-auto">
          <table className="text-xs min-w-full" data-testid="stokvel-report-table">
            <thead className="bg-stone-100 uppercase text-stone-600">
              <tr>
                <th className="text-left px-2 py-2 sticky left-0 bg-stone-100">Name</th>
                {data.months.map(m => <th key={m} className="text-right px-2 py-2 whitespace-nowrap">{monthLabel(m)}</th>)}
                {KEYS.map(([k, l]) => <th key={k} className="text-right px-2 py-2 whitespace-nowrap">{l}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.rows.map(r => (
                <tr key={r.membership_id} data-testid={`stokvel-report-row-${r.user_id}`}>
                  <td className="px-2 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">{r.name}</td>
                  {data.months.map(m => <td key={m} className="px-2 py-2 text-right font-mono whitespace-nowrap"><Cell v={r.months[m]} /></td>)}
                  {KEYS.map(([k]) => <td key={k} className="px-2 py-2 text-right font-mono">{rand(r.totals[k])}</td>)}
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-stone-50 font-semibold" data-testid="stokvel-report-totals">
                  <td className="px-2 py-2 sticky left-0 bg-stone-50">Totals</td>
                  {data.months.map(m => <td key={m} className="px-2 py-2 text-right font-mono whitespace-nowrap">
                    <Cell v={Object.fromEntries(KEYS.map(([k]) => [k, data.rows.reduce((s, r) => s + r.months[m][k], 0)]))} />
                  </td>)}
                  {KEYS.map(([k]) => <td key={k} className="px-2 py-2 text-right font-mono">{rand(data.rows.reduce((s, r) => s + r.totals[k], 0))}</td>)}
                </tr>
              )}
              {!data.rows.length && <tr><td colSpan={data.months.length + 7} className="text-center text-stone-400 py-6">No stokvel activity in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
